import { z } from 'zod';

/** Request validation shared by the API (authoritative) and the web app (early feedback). */

export const mondayId = z.string().regex(/^\d{1,20}$/, 'must be a numeric monday id');
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
  .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'invalid date');
export const itemName = z.string().trim().min(1, 'Name is required').max(255);
const label = z.string().trim().min(1).max(100);
const ids = z.array(mondayId).max(25);

/** Values previously seen by the client; used for optimistic concurrency (compare-and-set). */
const expected = z.record(z.string(), z.unknown()).optional();

// ---- Deliverables
export const deliverableFields = {
  name: itemName,
  productGoalId: mondayId.nullable(),
  epicId: mondayId.nullable(),
  pmOwnerIds: ids,
  assigneeIds: ids,
  sprint: label.nullable(),
  scopingStatus: label.nullable(),
  scopingDue: isoDate.nullable(),
  status: label.nullable(),
  type: label.nullable(),
};

export const createDeliverableSchema = z
  .object({ ...deliverableFields, name: itemName })
  .partial({
    productGoalId: true,
    epicId: true,
    pmOwnerIds: true,
    assigneeIds: true,
    sprint: true,
    scopingStatus: true,
    scopingDue: true,
    status: true,
    type: true,
  })
  .strict();
export type CreateDeliverable = z.infer<typeof createDeliverableSchema>;

export const patchDeliverableSchema = z
  .object({ ...deliverableFields, expected })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).filter((k) => k !== 'expected').length > 0, 'No changes supplied');
export type PatchDeliverable = z.infer<typeof patchDeliverableSchema>;

// ---- Product goals
export const goalFields = {
  name: itemName,
  targetDate: isoDate.nullable(),
  status: label.nullable(),
};
export const createGoalSchema = z.object({ ...goalFields, targetDate: goalFields.targetDate.optional(), status: goalFields.status.optional() }).strict();
export type CreateGoal = z.infer<typeof createGoalSchema>;
export const patchGoalSchema = z
  .object({ ...goalFields, expected })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).filter((k) => k !== 'expected').length > 0, 'No changes supplied');
export type PatchGoal = z.infer<typeof patchGoalSchema>;

// ---- Epics
export const epicFields = {
  name: itemName,
  productArea: label.nullable(),
  type: label.nullable(),
  pmOwnerIds: ids,
  rdOwnerIds: ids,
};
export const createEpicSchema = z
  .object({ ...epicFields })
  .partial({ productArea: true, type: true, pmOwnerIds: true, rdOwnerIds: true })
  .strict();
export type CreateEpic = z.infer<typeof createEpicSchema>;
export const patchEpicSchema = z
  .object({ ...epicFields, expected })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).filter((k) => k !== 'expected').length > 0, 'No changes supplied');
export type PatchEpic = z.infer<typeof patchEpicSchema>;

// ---- Tasks (subitems of a Deliverable)
export const taskFields = {
  name: itemName,
  status: label.nullable(),
  ownerIds: ids,
  date: isoDate.nullable(),
};
export const createTaskSchema = z.object({ ...taskFields }).partial({ status: true, ownerIds: true, date: true }).strict();
export type CreateTask = z.infer<typeof createTaskSchema>;
export const patchTaskSchema = z
  .object({ ...taskFields, expected })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).filter((k) => k !== 'expected').length > 0, 'No changes supplied');
export type PatchTask = z.infer<typeof patchTaskSchema>;

/** Route table: the web client and the API agree on these paths. */
export const ROUTES = {
  me: '/api/me',
  roadmap: '/api/roadmap',
  metadata: '/api/metadata',
  deliverables: '/api/deliverables',
  deliverable: (id: string) => `/api/deliverables/${id}`,
  deliverableTasks: (id: string) => `/api/deliverables/${id}/tasks`,
  task: (id: string) => `/api/tasks/${id}`,
  productGoals: '/api/product-goals',
  productGoal: (id: string) => `/api/product-goals/${id}`,
  epics: '/api/epics',
  epic: (id: string) => `/api/epics/${id}`,
  mondayStatus: '/api/admin/monday/status',
  mondayConnect: '/api/admin/monday/connect',
  /** POST {code, state} after monday redirects the admin back to the SPA route /monday/callback. */
  mondayCallback: '/api/admin/monday/callback',
} as const;

export const mondayCallbackSchema = z.object({ code: z.string().min(1).max(4096), state: z.string().min(1).max(8192) }).strict();
